import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  ActivityIndicator,
  Keyboard,
  Platform,
  Pressable,
  TextInput,
  View,
} from "react-native";
import { setItem } from "@lib/storage";
import {
  getBrand,
  getBuildNumber,
  getCarrier,
  getDevice,
  getDeviceId,
  getDeviceType,
  getFingerprint,
  getIpAddress,
  getSystemVersion,
  getUniqueId,
} from "react-native-device-info";
import { AppEventsLogger } from "react-native-fbsdk-next";
import color from "@constants/color";
import analytics from "@services/analytics";
import Text from "@components/CustomText";
import { actions } from "@reducers/authReducer";
import { useDispatch, useSelector } from "react-redux";
import { IDFA } from "@lib/idfa";
import { getFpRequestId } from "@lib/fingerprint";
import { androidAppStore } from "@config";
import {
  SIGNUP_FLOW_EVENTS,
  SIGNUP_FLOW_SOURCES,
} from "@constants/analyticsEventsCT";
import { api, sendOtpToUser } from "@services/api";
import getOrCreateWebDeviceId from "@utils/getOrCreateWebDeviceId";
import toast from "@utils/toast";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import { otpStyles as styles } from "./styles";
import type { LoginOTPNavigationProp, LoginStackParamList } from "./types";
import type { RouteProp } from "@react-navigation/native";

const OTP_LENGTH = 4;
const COUNTDOWN_SECONDS = 90;
// Facebook AppEvents standard event for completed sign-in.
const COMPLETE_SIGNIN = "fb_mobile_activate_app";

interface LoginOTPProps {
  navigation: LoginOTPNavigationProp;
  route: RouteProp<LoginStackParamList, "LoginOTP">;
}

const LoginOTP: React.FC<LoginOTPProps> = ({ navigation, route }) => {
  const { phoneNumber } = route.params;
  const [otp, setOtp] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const hiddenInputRef = useRef<TextInput>(null);
  const dispatch = useDispatch();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tracks whether the OTP was auto-filled from the SMS retriever vs typed by
  // the user. Mirrors the legacy EnterOTP.js parity — the SMS-retriever
  // listener is currently disabled there too, so this stays false in practice
  // but is wired so future re-enablement only needs the listener.
  const isAutoReadOtp = useRef(false);

  // Deeplink applink data from redux — surfaced into tracking_info so the
  // backend can attribute installs to install-referrer / applink campaigns.
  // useMemo wraps the `|| {}` fallback so the reference stays stable when the
  // selector returns undefined; otherwise the useCallback below would
  // re-create on every render.
  const applinkDataRaw = useSelector(
    (s: { deepLink?: { referer?: { hashResult?: { data?: object } } } }) =>
      s.deepLink?.referer?.hashResult?.data,
  );
  const applinkData = useMemo(() => applinkDataRaw || {}, [applinkDataRaw]);

  const isOtpComplete = otp.length === OTP_LENGTH;
  const canResend = countdown === 0;

  useEffect(() => {
    startCountdown();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // startCountdown is mount-only; intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCountdown = useCallback(() => {
    setCountdown(COUNTDOWN_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // One hidden TextInput drives 4 visual boxes. Per-box TextInputs + onKeyPress
  // are unreliable on Android — the soft keyboard only fires onKeyPress when
  // the EditText has content, so backspace in an empty box silently drops. A
  // single input always has content (or properly handles empty), so backspace
  // works via plain onChangeText.
  const onChangeOtp = useCallback((value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    setOtp(cleaned);
  }, []);

  const focusHiddenInput = useCallback(() => {
    hiddenInputRef.current?.focus();
  }, []);

  // Build the full verify-otp body — device fingerprint, idfa, install-referrer
  // applink data, user_type. The backend's createTlSession reads device_info /
  // idfa / uniqueDeviceId / auto_read_otp from req.body; missing fields land
  // as NULL on team_leader_sessions which weakens abuse-detection telemetry.
  // Matches the payload shape of legacy Auth/EnterOTP.js::getOtpPayload().
  const getOtpPayload = useCallback(async () => {
    const [device_fingerprint, carrier, device, ip] = await Promise.all([
      getFingerprint(),
      getCarrier(),
      getDevice(),
      getIpAddress(),
    ]);

    const deviceInfo = {
      device_fingerprint,
      device_type: getDeviceType(),
      carrier,
      device,
      os: Platform.OS,
      ip,
      ver: `${getBuildNumber()}`,
      os_version: `${getSystemVersion()}`,
      brand: getBrand(),
      model: getDeviceId(),
    };

    const idfa: string | null = await new Promise((resolve) => {
      IDFA.getIDFA()
        .then((deviceIdfa: string) => resolve(deviceIdfa))
        .catch(() => resolve(null));
    });

    const tracking_info = {
      ...applinkData,
      app_store: Platform.OS === "ios" ? "appstore" : androidAppStore,
    };

    // On web the device-info module is shimmed to a chainable no-op proxy, so
    // getUniqueId() returns a *truthy* thenable proxy (not a string) and idfa
    // resolves to null → without a fallback the session row stores NULL, leaving
    // web logins un-backtraceable. Await getUniqueId() so the proxy resolves to
    // undefined, then fall back to a persistent per-browser UUID for both fields.
    // Native is unaffected (real device values make the fallback never trigger).
    const webId = await getOrCreateWebDeviceId();

    return {
      phone_number: phoneNumber,
      device_info: JSON.stringify(deviceInfo),
      uniqueDeviceId: (await getUniqueId()) || webId,
      idfa: idfa || webId,
      tracking_info: JSON.stringify(tracking_info),
      user_type: "NON_TC_USER",
      enable_device_verification: false,
    };
  }, [phoneNumber, applinkData]);

  const verifyOtp = useCallback(async () => {
    if (isVerifying || !isOtpComplete) return;

    setIsVerifying(true);
    Keyboard.dismiss();

    try {
      const otpPayload = await getOtpPayload();
      // Device-fraud gate: fresh fingerprint per attempt; null (FP outage /
      // web) is fine — the backend fails open and logs the attempt.
      const fpRequestId = await getFpRequestId(phoneNumber, "login-verify-otp");
      const payload = {
        ...otpPayload,
        otp,
        auto_read_otp: isAutoReadOtp.current,
        source: "app",
        fp_request_id: fpRequestId ?? undefined,
      };

      const response = await api.post("/cpdb/verify-db-otp", payload);
      const { data = {} } = response || {};

      // Interceptors resolve 412/418 as success with the error body (see
      // CLAUDE.md High-Surprise Quirks). Treat absent token as failure.
      if (!data.token) {
        toast.showLongToast(
          data?.message || "OTP verification failed. Please try again.",
        );
        setOtp("");
        hiddenInputRef.current?.focus();
        return;
      }

      analytics.trackEvent(SIGNUP_FLOW_EVENTS.NUMBER_VERIFIED, {
        number_source: SIGNUP_FLOW_SOURCES.STANDARD,
      });
      analytics.trackEvent(SIGNUP_FLOW_EVENTS.SUCCESSFULL_LOGIN);
      AppEventsLogger.logEvent(COMPLETE_SIGNIN, { currency: "INR" });

      await setItem("user_token", data.token);

      dispatch(
        actions.signIn({
          verifyOtpResponse: data,
          userToken: data.token,
        }),
      );
      // The interceptor skips face_capture on login responses because cp_db_id
      // isn't in Redux state yet at this point. Dispatch it explicitly here so
      // the face capture modal fires on first login without requiring an app restart.
      if (data.face_capture) {
        dispatch(actions.setFaceCaptureData(data.face_capture));
      }
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      const message =
        axiosError?.response?.data?.message ||
        "OTP verification failed. Please try again.";
      toast.showLongToast(message);
      setOtp("");
      hiddenInputRef.current?.focus();
    } finally {
      setIsVerifying(false);
    }
  }, [isVerifying, isOtpComplete, otp, dispatch, getOtpPayload, phoneNumber]);

  const resendOtp = useCallback(async () => {
    if (!canResend) return;

    try {
      await sendOtpToUser(phoneNumber, __DEV__);
      startCountdown();
      toast.showLongToast("OTP sent successfully");
    } catch {
      toast.showLongToast("Failed to resend OTP. Please try again.");
    }
  }, [canResend, phoneNumber, startCountdown]);

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle="Verify OTP"
        navigation={navigation}
        hideShadow
      />

      <View style={styles.contentContainer}>
        <Text style={styles.title}>Welcome To CityMall Delivery!</Text>
        <Text variant="body_text_3" style={styles.subtitle}>
          Enter the OTP sent to your mobile number
        </Text>
        <Text style={styles.phoneNumber}>+91 {phoneNumber}</Text>

        <Pressable
          style={styles.otpContainer}
          onPress={focusHiddenInput}
          accessibilityRole="none"
        >
          {Array.from({ length: OTP_LENGTH }).map((_, index) => {
            const digit = otp[index] ?? "";
            const isActive = index === Math.min(otp.length, OTP_LENGTH - 1);
            return (
              <View
                key={index}
                style={[
                  styles.otpInput,
                  isActive && styles.otpInputFocused,
                  digit ? styles.otpInputFilled : undefined,
                  { pointerEvents: "none" },
                ]}
              >
                <Text style={styles.otpDigit}>{digit}</Text>
              </View>
            );
          })}
        </Pressable>

        <TextInput
          ref={hiddenInputRef}
          value={otp}
          onChangeText={onChangeOtp}
          keyboardType="number-pad"
          maxLength={OTP_LENGTH}
          caretHidden
          autoFocus
          style={styles.hiddenOtpInput}
          accessibilityLabel="OTP input"
          textContentType="oneTimeCode"
          autoComplete="sms-otp"
        />

        <View style={styles.timerContainer}>
          {canResend ? (
            <Pressable
              onPress={resendOtp}
              accessibilityRole="button"
              accessibilityLabel="Resend OTP"
            >
              <Text style={styles.resendText}>Resend</Text>
            </Pressable>
          ) : (
            <Text style={styles.timerText}>{formatTime(countdown)}</Text>
          )}
        </View>

        <Pressable
          style={[
            styles.verifyButton,
            {
              backgroundColor: isOtpComplete
                ? color.primary
                : color.lighterBlack,
            },
          ]}
          disabled={!isOtpComplete || isVerifying}
          onPress={verifyOtp}
          accessibilityRole="button"
          accessibilityLabel="Verify OTP"
        >
          <Text variant="title_16" style={styles.verifyButtonText}>
            Verify
          </Text>
        </Pressable>
      </View>

      {isVerifying && (
        <View style={styles.loadingOverlay} pointerEvents="auto">
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      )}
    </View>
  );
};

export default LoginOTP;
